import React, { useEffect, useState } from "react";
import { FileBarChart, Download, ArrowUpDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const RANGES = [
  ["today", "Today"], ["7d", "7 days"], ["30d", "30 days"],
  ["this_month", "This month"], ["last_month", "Last month"],
];

export default function Reports() {
  const [view, setView] = useState("financial");
  return (
    <div className="space-y-5" data-testid="admin-reports">
      <div className="flex items-center gap-2"><FileBarChart className="h-5 w-5 text-sky-400" /><h1 className="font-heading text-2xl font-bold">Reports</h1></div>
      <div className="flex gap-1 rounded-full border border-slate-800 p-1 w-max">
        {[["financial", "Financial"], ["merchants", "Merchant profitability"], ["subscriptions", "Subscriptions"]].map(([k, l]) => (
          <button key={k} data-testid={`reports-tab-${k}`} onClick={() => setView(k)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === k ? "bg-sky-500 text-slate-950" : "text-slate-400"}`}>{l}</button>
        ))}
      </div>
      {view === "financial" && <Financial />}
      {view === "merchants" && <Merchants />}
      {view === "subscriptions" && <Subscriptions />}
    </div>
  );
}

function Financial() {
  const [range, setRange] = useState("30d");
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/reports/financial?range=${range}`).then((r) => setD(r.data)).catch(() => toast.error("Failed")); }, [range]);
  return (
    <div className="space-y-4" data-testid="reports-financial">
      <div className="flex flex-wrap gap-2">
        {RANGES.map(([k, l]) => (
          <button key={k} data-testid={`range-${k}`} onClick={() => setRange(k)} className={`rounded-full border px-3 py-1.5 text-sm ${range === k ? "border-sky-500 text-sky-300" : "border-slate-700 text-slate-400"}`}>{l}</button>
        ))}
      </div>
      {!d ? <Spin /> : (
        <div className="ledger-card rounded-2xl p-6">
          <div className="space-y-1.5">
            <Line label="Gross revenue" value={d.gross_revenue.display} />
            <Line label="GST" value={`− ${d.gst.display}`} muted />
            <Line label="Net revenue" value={d.net_revenue.display} strong />
            <Line label="Cashback cost" value={`− ${d.cashback_cost.display}`} neg />
            <Line label="Payment fees" value={`− ${d.payment_fees.display}`} neg />
            <Line label="Refunds" value={`− ${d.refunds.display}`} neg />
            <Line label="Referral rewards" value={`− ${d.referral_cost?.display ?? "₹0.00"}`} neg />
            <div className="my-2 border-t border-slate-800" />
            <Line label="Net result" value={d.net_result.display} result={(d.net_result.paise ?? 0) >= 0} />
          </div>
        </div>
      )}
    </div>
  );
}

function Merchants() {
  const [rows, setRows] = useState(null);
  const [sort, setSort] = useState("net_contribution_paise");
  useEffect(() => { api.get("/admin/reports/merchants").then((r) => setRows(r.data.merchants)).catch(() => toast.error("Failed")); }, []);
  if (!rows) return <Spin />;
  const sorted = [...rows].sort((a, b) => b[sort] - a[sort]);
  const exportCsv = () => {
    const head = ["Merchant", "Subscriptions", "Gross", "GST", "Net", "Tokens", "Cashback", "Net contribution"];
    const lines = sorted.map((m) => [m.business_name, m.subscriptions, m.gross_paise / 100, m.gst_paise / 100, m.net_paise / 100, m.tokens_issued, m.cashback_paise / 100, m.net_contribution_paise / 100].join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "merchant_profitability.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success("CSV exported");
  };
  const SortBtn = ({ k, label }) => (
    <button onClick={() => setSort(k)} className={`inline-flex items-center gap-1 ${sort === k ? "text-sky-300" : ""}`}>{label} <ArrowUpDown className="h-3 w-3" /></button>
  );
  return (
    <div className="space-y-3" data-testid="reports-merchants">
      <div className="flex justify-end">
        <button data-testid="reports-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-sky-400"><Download className="h-4 w-4" /> Export CSV</button>
      </div>
      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reports-merchants-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4 text-left">Merchant</th><th className="p-4 text-right">Subs</th><th className="p-4 text-right"><SortBtn k="net_paise" label="Net rev" /></th><th className="p-4 text-right">Tokens</th><th className="p-4 text-right"><SortBtn k="cashback_paise" label="Cashback" /></th><th className="p-4 text-right"><SortBtn k="net_contribution_paise" label="Net contribution" /></th></tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <tr key={m.merchant_id} className="border-t border-slate-800/60">
                  <td className="p-4">{m.business_name}</td>
                  <td className="p-4 text-right font-mono">{m.subscriptions}</td>
                  <td className="p-4 text-right font-mono text-emerald-400">{m.net_display}</td>
                  <td className="p-4 text-right font-mono text-slate-400">{m.tokens_issued}</td>
                  <td className="p-4 text-right font-mono text-pink-300">{m.cashback_display}</td>
                  <td className={`p-4 text-right font-mono font-semibold ${m.net_contribution_paise >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{m.net_contribution_display}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Subscriptions() {
  const [plans, setPlans] = useState(null);
  useEffect(() => { api.get("/admin/reports/subscriptions").then((r) => setPlans(r.data.plans)).catch(() => toast.error("Failed")); }, []);
  if (!plans) return <Spin />;
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="reports-subscriptions">
      {plans.map((p) => (
        <div key={p.plan_id} className="ledger-card rounded-2xl p-5">
          <p className="font-heading text-lg font-bold">{p.name}</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-emerald-400">{p.count}</p>
          <p className="text-xs text-slate-500">active subscriptions</p>
          <div className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Net revenue</span><span className="font-mono">{p.net_display}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">GST</span><span className="font-mono text-slate-400">{p.gst_display}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Tokens</span><span className="font-mono">{p.tokens.toLocaleString("en-IN")}</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Line({ label, value, muted, strong, neg, result }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong || result ? "font-semibold text-slate-200" : "text-slate-400"}`}>{label}</span>
      <span className={`font-mono text-sm ${result !== undefined ? (result ? "text-emerald-400 font-bold text-base" : "text-rose-400 font-bold text-base") : neg ? "text-rose-300" : muted ? "text-slate-500" : strong ? "text-emerald-400 font-semibold" : "text-slate-100"}`}>{value}</span>
    </div>
  );
}
function Spin() { return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>; }
